import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, inp, label, card, btnPrimary } from "./AdminShell";
import { Save } from "lucide-react";

export function AdminHomepageSettings() {
  const [s, setS] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/admin/categories").then((r) => setCategories(r.data));
  }, []);

  if (!s) return <div className="text-neutral-500">Memuat...</div>;
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const toggleInArray = (k, val) => {
    const arr = s[k] || [];
    const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
    set(k, next);
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Homepage settings tersimpan."); }
    catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const airmanProducts = products.filter((p) => p.brand === "Airman");

  return (
    <div>
      <PageHeader title="Homepage Settings" subtitle="Konfigurasi konten halaman utama" />
      <form onSubmit={save} className="grid grid-cols-1 gap-4 max-w-4xl">
        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-4">Hero Section</h2>
          <div className="space-y-3">
            <div><label className={label}>Hero Image URL (rekomendasi 1600×1200)</label><input value={s.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className={inp} data-testid="set-hero_image" placeholder="Kosongkan untuk gunakan typography default" /></div>
            {s.hero_image && <img src={s.hero_image} alt="preview" className="w-full max-h-64 object-cover rounded-md border border-neutral-200" />}
            <div><label className={label}>Hero Heading (override default)</label><input value={s.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} className={inp} placeholder="Solusi Mesin & Peralatan Industri Terpercaya" /></div>
            <div><label className={label}>Hero Description</label><textarea rows={3} value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className={inp} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>CTA Text</label><input value={s.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} className={inp} placeholder="Lihat Produk" /></div>
              <div><label className={label}>CTA Link</label><input value={s.cta_link || ""} onChange={(e) => set("cta_link", e.target.value)} className={inp} placeholder="/produk" /></div>
            </div>
          </div>
        </div>

        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-2">Featured Products</h2>
          <p className="text-xs text-neutral-500 mb-3">Pilih produk yang tampil di section "Produk Unggulan" homepage (kosongkan untuk gunakan flag featured produk).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-80 overflow-y-auto border border-neutral-200 rounded-md p-2">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-neutral-50 rounded cursor-pointer text-sm">
                <input type="checkbox" checked={(s.featured_product_ids || []).includes(p.id)} onChange={() => toggleInArray("featured_product_ids", p.id)} className="accent-[#FF9418]" />
                <span className="flex-1 truncate">{p.product_name}</span>
                <span className="text-[10px] text-neutral-400 font-mono">{p.brand}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-2">Homepage Categories</h2>
          <p className="text-xs text-neutral-500 mb-3">Kategori yang tampil di section "Kategori Produk" (kosongkan = tampilkan semua published).</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {categories.map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-3 py-2 border border-neutral-200 rounded-md hover:bg-neutral-50 cursor-pointer text-sm">
                <input type="checkbox" checked={(s.homepage_categories || []).includes(c.name)} onChange={() => toggleInArray("homepage_categories", c.name)} className="accent-[#FF9418]" />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-2">Airman Feature Section</h2>
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input type="checkbox" checked={s.airman_feature_enabled !== false} onChange={(e) => set("airman_feature_enabled", e.target.checked)} className="accent-[#FF9418]" />
            <span className="text-sm">Tampilkan section highlight Airman di homepage</span>
          </label>
          <p className="text-xs text-neutral-500 mb-2">Model yang di-highlight (kosongkan untuk semua PDS series):</p>
          <div className="grid grid-cols-2 gap-1.5">
            {airmanProducts.map((p) => (
              <label key={p.id} className="flex items-center gap-2 px-3 py-2 border border-neutral-200 rounded-md hover:bg-neutral-50 cursor-pointer text-sm">
                <input type="checkbox" checked={(s.airman_feature_models || []).includes(p.id)} onChange={() => toggleInArray("airman_feature_models", p.id)} className="accent-[#FF9418]" />
                <span>{p.model}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-4">About & Workshop</h2>
          <div className="space-y-3">
            <div><label className={label}>About Summary (homepage)</label><textarea rows={4} value={s.about_summary || ""} onChange={(e) => set("about_summary", e.target.value)} className={inp} /></div>
            <div><label className={label}>Workshop / Service Image URL</label><input value={s.workshop_image || ""} onChange={(e) => set("workshop_image", e.target.value)} className={inp} /></div>
          </div>
        </div>

        <div>
          <Button type="submit" disabled={saving} className={`${btnPrimary} h-11 px-6`} data-testid="homepage-save"><Save size={14} /> {saving ? "Menyimpan..." : "Simpan Homepage"}</Button>
        </div>
      </form>
    </div>
  );
}
